/**
 * Countdown display driven by a Unix timestamp in seconds (matching Stripe's
 * `expiresAt` — seconds since epoch, not milliseconds). Ticks every second,
 * returns pre-formatted strings in both a short (mm:ss) and a long
 * (days/hours/minutes) form, and treats null/undefined targets as already
 * expired.
 */
import { useEffect, useState } from 'react';

export interface CountdownResult {
  /** Milliseconds remaining (clamped to 0) */
  msLeft: number;
  /** Whole seconds remaining (floor of msLeft/1000) */
  secsLeft: number;
  /** mm:ss formatted string, e.g. "29:42" */
  formatted: string;
  /**
   * Coarse day/hour/minute string for multi-day windows, e.g. "2d 14h 33m".
   * See {@link formatLongCountdown}.
   */
  formattedLong: string;
  /** True when secsLeft <= 0 */
  expired: boolean;
}

const SECS_PER_MINUTE = 60;
const SECS_PER_HOUR = 60 * SECS_PER_MINUTE;
const SECS_PER_DAY = 24 * SECS_PER_HOUR;

/**
 * Format a remaining-seconds count as days/hours/minutes for windows too long
 * to read as mm:ss — a producer/processor placement hold runs for DAYS, where
 * "4319:58" is unreadable and "2d 23h 59m" is not.
 *
 * Leading zero units are dropped ("14h 33m", not "0d 14h 33m") so the string
 * stays short enough for a chip, and the tail below one minute collapses to
 * "<1m" rather than claiming a rounded-up "1m" or a bare "0m".
 * @param secsLeft whole seconds remaining (clamped at 0 by the caller)
 * @returns the formatted string, e.g. "2d 14h 33m" / "14h 33m" / "33m" / "<1m"
 */
export function formatLongCountdown(secsLeft: number): string {
  if (secsLeft < SECS_PER_MINUTE) return '<1m';
  const days = Math.floor(secsLeft / SECS_PER_DAY);
  const hours = Math.floor((secsLeft % SECS_PER_DAY) / SECS_PER_HOUR);
  const mins = Math.floor((secsLeft % SECS_PER_HOUR) / SECS_PER_MINUTE);
  const parts: string[] = [];
  if (days > 0) parts.push(`${days}d`);
  if (days > 0 || hours > 0) parts.push(`${hours}h`);
  parts.push(`${mins}m`);
  return parts.join(' ');
}

const EXPIRED_RESULT: CountdownResult = {
  msLeft: 0,
  secsLeft: 0,
  formatted: '0:00',
  formattedLong: '0m',
  expired: true,
};

function buildResult(targetTimestampMs: number): CountdownResult {
  const msLeft = Math.max(0, targetTimestampMs - Date.now());
  const secsLeft = Math.floor(msLeft / 1000);
  const mins = Math.floor(secsLeft / 60);
  const secs = secsLeft % 60;
  const formatted = `${mins}:${String(secs).padStart(2, '0')}`;
  const expired = secsLeft <= 0;
  return {
    msLeft,
    secsLeft,
    formatted,
    formattedLong: expired ? '0m' : formatLongCountdown(secsLeft),
    expired,
  };
}

/**
 * Normalize a wire-format expiry (ISO string or a numeric seconds timestamp —
 * both appear for Split.holdExpiresAt) to Unix seconds for {@link useCountdown}.
 * Returns null when absent/invalid.
 * @param value
 */
export function toEpochSeconds(
  value: string | number | null | undefined,
): number | null {
  if (value == null) return null;
  const asNum = Number(value);
  if (!Number.isNaN(asNum) && asNum > 1_000_000_000) return asNum;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : Math.floor(ms / 1000);
}

/**
 * Count down to a Unix timestamp expressed in seconds (e.g. Stripe
 * `expiresAt`); null/undefined means "already expired". Updates every second.
 * @param targetDate
 */
export function useCountdown(
  targetDate: number | null | undefined,
): CountdownResult {
  const [result, setResult] = useState<CountdownResult>(() => {
    if (targetDate == null) return EXPIRED_RESULT;
    return buildResult(targetDate * 1000);
  });

  useEffect(() => {
    if (targetDate == null) {
      setResult(EXPIRED_RESULT);
      return undefined;
    }

    const targetMs = targetDate * 1000;

    const tick = () => {
      setResult(buildResult(targetMs));
    };

    // Run immediately so the display is correct before the first tick fires.
    tick();

    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [targetDate]);

  return result;
}
